import { useEffect, useMemo, useState } from 'react';

import {
  ChakraBox,
  ChakraFlex,
  FAIconName,
  FireflyIconButton,
  ModalV2,
  Tab,
  Text,
} from '@equip.health/ui';
import { DateTime } from 'luxon';
import { useHistory, useLocation } from 'react-router-dom';

import {
  AppointmentCancellationEvents,
  EditAppointmentSource,
  PatientEvents,
} from '~/analytics/patientEvents';
import ScheduleView from '~/components/common/calendar/schedule/ScheduleView';
import { ApiCommand } from '~/lib/Api';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';
import {
  AppointmentStatus,
  ZOOM_USER_DOES_NOT_EXIST_ERROR_TEXT,
} from '~/lib/constants/calendar.constants';
import { GlobalErrorCode } from '~/lib/constants/global.constants';
import { MaudPermissions } from '~/lib/constants/permissions.constants';
import urlConstants from '~/lib/constants/url.constants';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import {
  PortalComponent,
  useComponentPortalContext,
} from '~/lib/contexts/ComponentPortalContext';
import { useGlobalContext } from '~/lib/contexts/GlobalContext';
import { useApi } from '~/lib/hooks';
import useErrorToast from '~/lib/hooks/useErrorToast';
import { useConsolidatedScheduleItems } from '~/lib/hooks/useConsolidatedScheduleItems';
import { getAppointmentFromExternalId } from '~/lib/util/appointment.util';
import {
  getFetchEventsDateFilters,
  getProviderUniversalSelectOption,
  getScheduleViewDateHeader,
  ScheduleViewTab,
} from '~/lib/util/calendar.util';
import {
  deriveScheduleItems,
  getLastScheduleEventStartDate,
  getNextScheduleEventStartDate,
  getScheduleItemFromExternalId,
  ScheduleType,
} from '~/lib/util/schedule/schedule.util';
import Loading from '~/pages/loading';
import { Path } from '~/routes/constants';
import {
  useGetGroupClassEventsQuery,
  useGetGroupSectionEventDatesQuery,
} from '~/store/services/groupClass';
import { useGetGroupClassNotesByEventExternalIdsQuery } from '~/store/services/notes/notes';

import { useGetAppointmentsWithNotesQuery } from '~/store/services/appointment';
import EventDayCard from '~/components/common/calendar/schedule/day-view/EventDayCard';
import { useEditNoteDrawerContext } from '~/lib/contexts/EditNoteDrawerContext';
import { PatientTabs } from '~/lib/constants/patients.constants';
import { PatientTabsCollection } from '~/lib/hooks/usePatientTabsCollection';
import ProviderUniversalSelect from '~/components/common/calendar/ProviderUniversalSelect';
import { CalendarListTypeTitle } from '../../patients/profile/tabs/appointments/NewAppointmentHeader';
import CancelAppointmentModal from '~/components/maud/calendar/schedule/modals/CancelAppointmentModal';
import ScheduleFiltersPopover from '~/components/maud/calendar/schedule/ScheduleFiltersPopover';
import { useGetAllProvidersV2Query } from '~/store/services/providers';
import { useGroupEventMeetingDetails } from '~/lib/hooks/useGroupEventMeetingDetails';
import { useFlags } from 'launchdarkly-react-client-sdk';

const TOP_LEVEL_ID = 'calendar-schedule';

const {
  calendar: { getMeetingDetails: getAppointmentZoomMeetingDetailsUrl },
} = urlConstants;

interface ScheduleHeaderProps {
  filterByKey: 'provider-external-id' | 'patient-external-id';
  filterByValue: string;
  participantType: ParticipantType;
  currentDate: DateTime;
  setCurrentDate: React.Dispatch<React.SetStateAction<DateTime>>;
}

const tabIndexToPeriodMapper: Record<ScheduleViewTab, string> = {
  [ScheduleViewTab.DAY]: 'days',
  [ScheduleViewTab.WEEK]: 'weeks',
};

const ScheduleHeader = ({
  filterByKey,
  filterByValue,
  currentDate,
  setCurrentDate,
}: ScheduleHeaderProps) => {
  const { pathname } = useLocation();
  const { trackEvent } = useAnalytics();
  const { currentUser, permissions } = useGlobalContext();
  const { openGroupEventMeeting } = useGroupEventMeetingDetails();

  const {
    groupsRestructuring: isGroupsRestructuringEnabled,
    viewCancelledAppointmentsInMaud: isViewCancelledAppointmentsInMaudEnabled,
  } = useFlags();

  const isSchedulingAdmin = permissions[MaudPermissions.MAUD_SCHEDULE_PROXY];

  const { setComponentVisibility } = useComponentPortalContext();
  const { openNoteDrawer } = useEditNoteDrawerContext();

  const { data: providersData } = useGetAllProvidersV2Query({
    id: [currentUser.userId],
    'include-out-of-office': true,
    'page-number': 1,
    'page-size': 10,
    status: 'active',
  });
  const { showErrorToast } = useErrorToast();

  const [tabIndex, setTabIndex] = useState<number>(0);
  const [selectedProviderValue, setSelectedProviderValue] =
    useState<UniversalSelectOption | null>(null);
  const [selectedEventId, setSelectedEventId] = useState<string>(null);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState<boolean>(false);

  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState<boolean>(false);
  const [showCancelledAppointments, setShowCancelledAppointments] =
    useState<boolean>(false);

  const [groupClassEventIds, setGroupClassEventIds] = useState<string[]>([]);

  const { startDate, endDate } = useMemo(() => {
    return getFetchEventsDateFilters(tabIndex, currentDate);
  }, [tabIndex, currentDate]);

  const { data: appointmentsNotesData, isFetching: appointmentsLoading } =
    useGetAppointmentsWithNotesQuery({
      endDate,
      filterByKey,
      filterByValue: selectedProviderValue?.value ?? filterByValue,
      includeTimezoneDetails: true,
      pageSize: 200,
      startDate,
      ...(isViewCancelledAppointmentsInMaudEnabled
        ? {}
        : {
            statuses: [
              AppointmentStatus.COMPLETED,
              AppointmentStatus.SCHEDULED,
            ],
          }),
      shouldIncludeCancelled: showCancelledAppointments,
    });
  const appointmentDetails =
    appointmentsNotesData?.appointmentDetails as GetAppointmentDetailsResponse;

  const {
    data: groupClassEventsData,
    isLoading: isGetGroupClassEventsLoading,
  } = useGetGroupClassEventsQuery(
    {
      endDateTime: endDate,
      providerExternalId: selectedProviderValue?.value,
      showDetailed: false,
      startDateTime: startDate,
    },
    {
      skip: isGroupsRestructuringEnabled,
    },
  );

  const { data: groupSectionEventDatesData } =
    useGetGroupSectionEventDatesQuery(
      {
        providerExternalId: selectedProviderValue?.value,
        startDate,
        endDate,
      },
      {
        skip: !isGroupsRestructuringEnabled || !selectedProviderValue?.value,
      },
    );

  const {
    data: groupClassEventNotes,
    isLoading: isGetGroupClassEventNotesLoading,
  } = useGetGroupClassNotesByEventExternalIdsQuery(groupClassEventIds, {
    skip: groupClassEventIds?.length === 0 || isGroupsRestructuringEnabled,
  });

  useEffect(() => {
    if (providersData?.data?.length > 0) {
      setSelectedProviderValue(
        getProviderUniversalSelectOption(providersData.data[0]),
      );
    }
  }, [providersData?.data]);
  useEffect(() => {
    setGroupClassEventIds(
      groupClassEventsData?.data?.map((gce) => gce.groupClassEventExternalId) ||
        [],
    );
  }, [groupClassEventsData?.data]);

  const scheduleItems = useMemo<ScheduleItemBase[]>(() => {
    return deriveScheduleItems(
      selectedProviderValue?.value ?? filterByValue,
      appointmentDetails?.data,
      groupClassEventsData?.data,
      [
        ...(groupClassEventNotes ?? []),
        ...(appointmentsNotesData?.notes ?? []),
      ],
      isViewCancelledAppointmentsInMaudEnabled,
    );
  }, [
    appointmentDetails?.data,
    groupClassEventsData?.data,
    groupClassEventNotes,
    appointmentsNotesData?.notes,
    selectedProviderValue?.value,
    filterByValue,
    isViewCancelledAppointmentsInMaudEnabled,
  ]);

  const { consolidatedScheduleItems, isLoading } = useConsolidatedScheduleItems(
    {
      scheduleItems,
      providerId: selectedProviderValue?.value,
      filterByValue,
      startDateTime: startDate,
      endDateTime: endDate,
      pageSize: 200,
    },
  );

  const selectedScheduleItem = useMemo<ScheduleItemBase>(
    () =>
      getScheduleItemFromExternalId(consolidatedScheduleItems, selectedEventId),
    [consolidatedScheduleItems, selectedEventId],
  );

  const nextScheduleDate = useMemo<string>(() => {
    return getNextScheduleEventStartDate(
      appointmentDetails?.nextAppointmentStartDate,
      isGroupsRestructuringEnabled
        ? groupSectionEventDatesData?.nextEventStartDate
        : groupClassEventsData?.nextEventStartDate,
    );
  }, [
    appointmentDetails?.nextAppointmentStartDate,
    isGroupsRestructuringEnabled
      ? groupSectionEventDatesData?.nextEventStartDate
      : groupClassEventsData?.nextEventStartDate,
  ]);
  const lastScheduleDate = useMemo<string>(() => {
    return getLastScheduleEventStartDate(
      appointmentDetails?.lastAppointmentStartDate,
      isGroupsRestructuringEnabled
        ? groupSectionEventDatesData?.lastEventStartDate
        : groupClassEventsData?.lastEventStartDate,
    );
  }, [
    appointmentDetails?.lastAppointmentStartDate,
    isGroupsRestructuringEnabled
      ? groupSectionEventDatesData?.lastEventStartDate
      : groupClassEventsData?.lastEventStartDate,
  ]);

  const handleCloseAndResetCancelModal = () => {
    setIsCancelModalOpen(false);
  };

  const { sendRequest: getAppointmentMeetingDetails } =
    useApi<MeetingDetails>();

  const onOpenZoom = (eventId: string, source: string): void => {
    let eventToTrack: PatientEvents;
    switch (source) {
      case AnalyticsSourceValues.CALENDAR_TAB:
        eventToTrack = PatientEvents.openZoomCalendarTabButtonClicked;
        break;
      case AnalyticsSourceValues.CALENDAR_TAB_MODAL:
        eventToTrack = PatientEvents.openZoomCalendarTabModalButtonClicked;
        break;
    }
    trackEvent(eventToTrack, {
      Source: AnalyticsSourceValues.CALENDAR_TAB,
    });
    if (
      getScheduleItemFromExternalId(consolidatedScheduleItems, eventId)
        .typeOf === 'Appointment'
    ) {
      getAppointmentMeetingDetails({
        callback: (
          meetingDetails: MeetingDetails,
          statusCode: number,
          error: string,
        ) => {
          if (statusCode === 200) {
            window.open(
              meetingDetails.startUrl ?? meetingDetails.joinUrl,
              '_blank',
              'noopener',
            );
          } else {
            showErrorToast(
              error === ZOOM_USER_DOES_NOT_EXIST_ERROR_TEXT
                ? GlobalErrorCode.ZOOM_USER_DOES_NOT_EXIST
                : GlobalErrorCode.GET_ZOOM_MEETING_DETAILS,
              statusCode,
              error,
            );
          }
        },
        command: ApiCommand.POST,
        options: {
          appointmentExternalId: eventId,
        },
        url: getAppointmentZoomMeetingDetailsUrl,
      });
    } else {
      openGroupEventMeeting(eventId);
    }
  };

  /** Get newly selected date and call another function to fetch events */
  const onPrevNextPage = (pageDirection: number): void => {
    const newDate = currentDate.plus({
      [tabIndexToPeriodMapper[tabIndex]]: pageDirection,
    });
    setCurrentDate(newDate);
  };

  const openGroupClassEventNoteDrawer = (eventId: string) => {
    let groupClassInitialData = null;
    let existingNote = null;

    if (isGroupsRestructuringEnabled) {
      const groupEvents = consolidatedScheduleItems.filter(
        (item) => item.groupClassExternalId,
      );
      groupClassInitialData = groupEvents.find((e) => e.externalId === eventId);
    } else {
      groupClassInitialData = groupClassEventsData?.data?.find(
        (e) => e.groupClassEventExternalId === eventId,
      ) as GroupClassEventDetails;
      existingNote = groupClassEventNotes?.find(
        (note) =>
          note.groupClassEventExternalId ===
          groupClassInitialData?.groupClassEventExternalId,
      );
    }

    if (groupClassInitialData) {
      setComponentVisibility({
        component: PortalComponent.NOTE_ACCORDION,
        data: {
          existingNoteId: isGroupsRestructuringEnabled
            ? groupClassInitialData?.noteExternalId
            : existingNote?.noteExternalId,
          existingNoteStatus: isGroupsRestructuringEnabled
            ? groupClassInitialData?.noteStatus
            : existingNote?.noteStatus,
          groupClassEventExternalId: isGroupsRestructuringEnabled
            ? eventId
            : groupClassInitialData?.groupClassEventExternalId,
          groupClassEventTitle: groupClassInitialData?.title,
        },
        isExpanded: true,
        isVisible: true,
      });
    }
  };

  const history = useHistory();

  const goToPatientProfileAndViewNote = (
    eventId: string,
    patientId: string,
  ) => {
    history.push({
      pathname: `${Path.MAUD_PATIENTS}/${patientId}/${
        PatientTabsCollection[PatientTabs.NOTES].subPath
      }`,
      state: {
        appointment: getAppointmentFromExternalId(
          appointmentDetails?.data,
          eventId,
        ),
      },
    });
  };

  const onNotesClick = (eventId: string) => {
    const scheduledItem = getScheduleItemFromExternalId(
      consolidatedScheduleItems,
      eventId,
    );

    if (scheduledItem) {
      const { hosts, isNoteInSignedState, patient, isNoteInDraftState } =
        scheduledItem;

      if (patient) {
        if (isNoteInSignedState) {
          goToPatientProfileAndViewNote(eventId, patient.userId);
        } else {
          const apptDetails = appointmentDetails?.data?.find(
            (a) => a.appointmentExternalId === eventId,
          );

          const note = (appointmentsNotesData?.notes ?? [])?.find(
            (note) => note.appointmentExternalId === eventId,
          );

          openNoteDrawer(patient.userId, note?.noteExternalId, apptDetails);
          history.push(`${Path.MAUD_PATIENTS}/${patient.userId}`);
        }
      } else if (
        // TODO can we simplify this conditional?
        isNoteInDraftState ||
        isNoteInSignedState ||
        hosts.some(({ externalId }) => externalId === currentUser.userId)
      ) {
        openGroupClassEventNoteDrawer(eventId);
        setIsDetailsModalOpen(false);
      }
    }
  };

  const onCloseGroupClassEventDetailModal = () => {
    setIsDetailsModalOpen(false);
  };

  const handleNextDateClick = (nextScheduleDate: string) => {
    setCurrentDate(DateTime.fromISO(nextScheduleDate));
  };

  const handleCancelledGroupClick = (startDateTime: string) => {
    setCurrentDate(DateTime.fromISO(startDateTime).startOf('day'));
    setTabIndex(ScheduleViewTab.DAY);
  };

  const scheduleTabs = useMemo(
    () => ({
      [ScheduleViewTab.DAY]: {
        listType: ScheduleViewTab.DAY,
        title: CalendarListTypeTitle.DAILY_VIEW,
      },
      [ScheduleViewTab.WEEK]: {
        listType: ScheduleViewTab.WEEK,
        title: CalendarListTypeTitle.WEEKLY_VIEW,
      },
    }),
    [],
  );

  const handleCancelAppointmentClick = (eventId: string) => {
    const scheduleItem = getScheduleItemFromExternalId(
      consolidatedScheduleItems,
      eventId,
    );

    setSelectedEventId(eventId);
    setIsCancelModalOpen(true);

    trackEvent(
      AppointmentCancellationEvents.cancelAppointmentKebabMenuClicked,
      {
        appointmentId: scheduleItem?.externalId,
        appointmentTitle: scheduleItem?.title,
        isRecurringEvent: scheduleItem?.isRecurring,
        Source: EditAppointmentSource.providerCalendarTab,
      },
    );
  };

  const handleCancelAppointmentModalClick = () => {
    if (selectedScheduleItem) {
      trackEvent(
        AppointmentCancellationEvents.cancelAppointmentKebabMenuClicked,
        {
          appointmentId: selectedScheduleItem.externalId,
          appointmentTitle: selectedScheduleItem.title,
          isRecurringEvent: selectedScheduleItem.isRecurring,
          Source: EditAppointmentSource.providerCalendarTab,
        },
      );
    }
    setIsCancelModalOpen(true);
    setIsDetailsModalOpen(false);
  };
  return (
    <ChakraFlex
      direction="column"
      height="100%"
      id={TOP_LEVEL_ID}
      paddingTop="27.5px"
    >
      <ChakraFlex
        align="center"
        justify="space-between"
        paddingBottom="24px"
        paddingRight="24px"
      >
        <ChakraFlex align="center" gap="8px">
          {Object.values(scheduleTabs).map((tab) => (
            <Tab
              isActive={tabIndex === tab.listType}
              key={tab.title}
              onClick={() => {
                setTabIndex(tab.listType);
              }}
              title={tab.title}
              variant="bubble"
            />
          ))}
        </ChakraFlex>

        <ChakraFlex align="center" gap="18px" height="42px" zIndex={2}>
          <Text
            color="primary.90.variant"
            id={`${TOP_LEVEL_ID}__period`}
            variant="body"
          >
            {getScheduleViewDateHeader(tabIndex, currentDate)}
          </Text>
          <ChakraFlex gap="10px">
            <ChakraBox cursor="pointer" margin="auto">
              <FireflyIconButton
                icon={FAIconName.arrowLeftRegular}
                id={`${TOP_LEVEL_ID}__create-group-class-weekely-leftarrow`}
                onClick={() => onPrevNextPage(-1)}
                variant="tertiary"
              />
            </ChakraBox>
            <ChakraBox cursor="pointer" margin="auto">
              <FireflyIconButton
                icon={FAIconName.arrowRightRegular}
                id={`${TOP_LEVEL_ID}__create-group-class-weekely-rightarrow`}
                onClick={() => onPrevNextPage(1)}
                variant="tertiary"
              />
            </ChakraBox>
          </ChakraFlex>

          {isSchedulingAdmin && pathname === Path.CALENDAR_SCHEDULE && (
            <ChakraBox
              background="neutral.background.primary"
              maxWidth="360px"
              minWidth="275px"
            >
              <ProviderUniversalSelect
                handleSelect={(providerOption: ProviderUniversalSelectOption) =>
                  setSelectedProviderValue(providerOption)
                }
                id={`${TOP_LEVEL_ID}__provider-universal-select`}
                modalId={`${TOP_LEVEL_ID}__provider-universal-select__universal-select-modal`}
                selectedProvider={selectedProviderValue}
              />
            </ChakraBox>
          )}
          {isViewCancelledAppointmentsInMaudEnabled && (
            <ScheduleFiltersPopover
              id={`${TOP_LEVEL_ID}__filters`}
              onShowCancelledAppointmentsChange={setShowCancelledAppointments}
              shouldShowCancelledAppointments={showCancelledAppointments}
            />
          )}
        </ChakraFlex>
      </ChakraFlex>
      {appointmentsLoading ||
      isGetGroupClassEventsLoading ||
      isGetGroupClassEventNotesLoading ||
      isLoading ? (
        <Loading />
      ) : (
        <ChakraBox
          height="calc(100vh - 208px)"
          overflow="auto"
          paddingRight="24px"
        >
          <ScheduleView
            currentDate={currentDate}
            height="calc(100vh - 208px)"
            isProvider
            lastScheduleDate={lastScheduleDate}
            mode={tabIndex}
            nextScheduleDate={nextScheduleDate}
            onCancelAppointment={handleCancelAppointmentClick}
            onCancelledGroupClicked={handleCancelledGroupClick}
            onCloseEventDetailsModal={onCloseGroupClassEventDetailModal}
            onNextScheduleDateClicked={handleNextDateClick}
            onNotesClicked={onNotesClick}
            onScheduleItemClicked={(eventId: string) => {
              setIsDetailsModalOpen(true);
              setSelectedEventId(eventId);
            }}
            onZoomClicked={(eventId: string) =>
              onOpenZoom(eventId, AnalyticsSourceValues.CALENDAR_TAB)
            }
            scheduledItems={consolidatedScheduleItems}
          />
        </ChakraBox>
      )}
      <ModalV2
        id={`${TOP_LEVEL_ID}__event-details`}
        isOpen={
          isDetailsModalOpen &&
          selectedScheduleItem?.typeOf === ScheduleType.GROUP_CLASS_EVENT
        }
        onClose={() => setIsDetailsModalOpen(false)}
        size="5xl"
        title="Event Details"
      >
        <EventDayCard
          key={selectedScheduleItem?.externalId}
          onCancelAppointment={() => {
            setIsCancelModalOpen(true);
            setIsDetailsModalOpen(false);
          }}
          onNextScheduleDateClicked={handleNextDateClick}
          onNoteButtonClick={onNotesClick}
          onZoomClick={(eventId: string) =>
            onOpenZoom(eventId, AnalyticsSourceValues.CALENDAR_TAB_MODAL)
          }
          scheduleItem={selectedScheduleItem}
          shouldCloseModal={onCloseGroupClassEventDetailModal}
        />
      </ModalV2>

      <ModalV2
        id={`${TOP_LEVEL_ID}__appointment-details`}
        isOpen={
          isDetailsModalOpen &&
          !isCancelModalOpen &&
          selectedScheduleItem?.typeOf === ScheduleType.APPOINTMENT
        }
        onClose={() => setIsDetailsModalOpen(false)}
        size="5xl"
        title="Appointment Details"
      >
        <EventDayCard
          key={selectedScheduleItem?.externalId}
          onCancelAppointment={handleCancelAppointmentModalClick}
          onNextScheduleDateClicked={handleNextDateClick}
          onNoteButtonClick={onNotesClick}
          onZoomClick={(eventId: string) =>
            onOpenZoom(eventId, AnalyticsSourceValues.CALENDAR_TAB_MODAL)
          }
          scheduleItem={selectedScheduleItem}
          shouldCloseModal={onCloseGroupClassEventDetailModal}
        />
      </ModalV2>
      <CancelAppointmentModal
        appointmentExternalId={selectedScheduleItem?.externalId}
        appointmentTitle={selectedScheduleItem?.title}
        isOpen={isCancelModalOpen}
        isRecurring={selectedScheduleItem?.isRecurring}
        onClose={handleCloseAndResetCancelModal}
      />
    </ChakraFlex>
  );
};

export default ScheduleHeader;
